import { ensureNotNil } from '@studio/js-utils';
import { χref } from '@studio/react-utils/hooks';
import { noop } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import type { OutputProps } from './types.js';
import { Volume } from '#~/audio/components/volume/index.js';
import { sound } from '#~/audio/sound/index.js';
import { useAudioTrack } from '#~/livekit/hooks.js';

/** `<Output>` component. */
export const Output = ({ deviceId, ...props }: OutputProps): ReactElement => {
  const track = useAudioTrack(deviceId);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!track) {
      return noop;
    }

    // Create a media stream from the track.
    const stream = new MediaStream([track.mediaStreamTrack]);

    // Create a media stream source from the stream.
    const node = sound.context.createMediaStreamSource(stream);

    // Hook up the node to the master fader.
    node.connect(sound.master);

    // Workaround for Chrome silent audio issue
    const audioElement = ensureNotNil(χref(audioRef));

    audioElement.srcObject = stream;

    return () => {
      audioElement.srcObject = null;

      // Clean up the media stream.
      stream.removeTrack(track.mediaStreamTrack);
      node.disconnect();
    };
  }, [track, track?.mediaStreamTrack]);

  // Render master audio output and volume control.
  return (
    <div>
      <audio className='hidden' ref={audioRef} />
      <Volume {...props} />
    </div>
  );
};
